/**
 * Sign in with Google, through Google Identity Services.
 *
 * GIS is a script rather than a package, so this needs no dependency — what it needs is
 * a client id, which is public but build-specific. Absent one, `googleSignInAvailable`
 * is false and the button is not offered: a button that opens Google and then fails at
 * our server is worse than one that is not there.
 *
 * What crosses the wire is the ID token Google signed, never a user id we read out of
 * it. The server verifies that token against Google's published keys, checks the issuer,
 * the expiry and — the one most often skipped — the audience, because a valid Google
 * token minted for somebody else's app says nothing about whether its holder is ours.
 */

const SCRIPT = 'https://accounts.google.com/gsi/client';

interface GoogleIdentity {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: { credential?: string }) => void;
        ux_mode?: 'popup' | 'redirect';
        auto_select?: boolean;
      }): void;
      prompt(): void;
    };
  };
}

export function googleClientId(): string | null {
  const id = import.meta.env?.VITE_GOOGLE_CLIENT_ID;
  return id === undefined || id === '' ? null : id;
}

export function googleSignInAvailable(): boolean {
  return googleClientId() !== null;
}

/** Loads the GIS script once, resolving null if it cannot be reached. */
async function load(): Promise<GoogleIdentity | null> {
  const existing = (globalThis as { google?: GoogleIdentity }).google;
  if (existing !== undefined) return existing;

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    // An ad blocker or an offline tab is a normal outcome here, not an error worth
    // throwing: the caller shows the same message either way.
    script.onerror = () => resolve(null);
    script.onload = () => resolve((globalThis as { google?: GoogleIdentity }).google ?? null);
    document.head.appendChild(script);
  });
}

/**
 * Prompts for a Google account and returns the signed ID token.
 *
 * Null means the learner dismissed it or the script could not load, both of which are
 * answers rather than failures.
 */
export async function googleIdToken(): Promise<string | null> {
  const clientId = googleClientId();
  if (clientId === null) return null;

  const google = await load();
  if (google === null) return null;

  return new Promise((resolve) => {
    // A dismissal fires no callback, so the promise would hang forever without this.
    const timeout = setTimeout(() => resolve(null), 120_000);
    google.accounts.id.initialize({
      client_id: clientId,
      // Never silent: linking an identity to an account is a decision, and
      // `auto_select` would make it for them.
      auto_select: false,
      ux_mode: 'popup',
      callback: (response) => {
        clearTimeout(timeout);
        resolve(response.credential ?? null);
      },
    });
    google.accounts.id.prompt();
  });
}
